import { Changeset } from 'ember-changeset';
import type { DetailedChangeset, ValidatorMapFunc } from 'ember-changeset/types';
import lookupValidator from 'ember-changeset-validations';
import { validateFormat, validatePresence } from 'ember-changeset-validations/validators';
import type GlobalSummaryPage from 'garaje/models/global-summary-page';
import type SummaryAttribute from 'garaje/models/summary-attribute';
import { SummaryAttributeState } from 'garaje/models/summary-attribute';
import type SummaryPage from 'garaje/models/summary-page';
import type { ModelData } from 'garaje/utils/type-utils';
import validateConditional from 'garaje/validators/conditional';

/** The per-state SummaryAttribute changesets edited by the final-screen tabs. */
export type SummaryAttributeChangesets = Partial<Record<SummaryAttributeState, DetailedChangeset<SummaryAttribute>>>;

export const SUMMARY_ATTRIBUTE_STATES: SummaryAttributeState[] = [
  SummaryAttributeState.Review,
  SummaryAttributeState.Denied,
];

/**
 * Validations for a SummaryAttribute. These mirror the validation applied to a
 * SummaryPage on the final screen editor:
 *  - `videoUrl` must be a valid URL when `kind` is "video"
 *  - `message` must be present when `kind` is "message"
 *  - `image` must be present when `kind` is "image"
 */
export function buildSummaryAttributeValidations(): Record<string, ValidatorMapFunc[]> {
  return {
    videoUrl: validateConditional({
      if(_key: string, _newValue: unknown, _oldValue: unknown, changes: unknown, content: object): boolean {
        const model = content as SummaryAttribute;
        const kind = (changes as ModelData<SummaryAttribute>).kind;
        return (kind || model.kind) === 'video';
      },
      then: validateFormat({
        type: 'url',
        allowBlank: false,
      }),
      else(): boolean {
        // Mark this field as valid. We don't need to validate since we
        // don't use the location value.
        return true;
      },
    }),
    message: validateConditional({
      if(_key: string, _newValue: unknown, _oldValue: unknown, changes: unknown, content: object): boolean {
        const model = content as SummaryAttribute;
        const kind = (changes as ModelData<SummaryAttribute>).kind;
        return (kind || model.kind) === 'message';
      },
      then: validatePresence(true),
      else(): boolean {
        // Mark this field as valid. We don't need to validate since we
        // don't use the location value.
        return true;
      },
    }),
    image: validateConditional({
      if(_key: string, _newValue: unknown, _oldValue: unknown, changes: unknown, content: object): boolean {
        const model = content as SummaryAttribute;
        const kind = (changes as ModelData<SummaryAttribute>).kind;
        return (kind || model.kind) === 'image';
      },
      then: validatePresence(true),
      else(): boolean {
        // Mark this field as valid. We don't need to validate since we
        // don't use the location value.
        return true;
      },
    }),
  };
}

/**
 * Factory for creating a Changeset used to validate SummaryAttributes.
 *
 * @param summaryAttribute - the SummaryAttribute model to wrap
 * @returns a Changeset configured with the SummaryAttribute validations
 */
export function buildSummaryAttributeChangeset(
  summaryAttribute: SummaryAttribute,
): DetailedChangeset<SummaryAttribute> {
  const validations = buildSummaryAttributeValidations();
  const validator = lookupValidator(validations);

  return Changeset(summaryAttribute, validator, validations);
}

/**
 * Build a changeset for each per-state SummaryAttribute, keyed by state.
 *
 * @param summaryPage - the summary page whose attributes to wrap
 * @returns the changesets keyed by state
 */
export function buildSummaryAttributeChangesets(
  summaryPage: SummaryPage | GlobalSummaryPage,
): SummaryAttributeChangesets {
  const changesets: SummaryAttributeChangesets = {};

  if (summaryPage.hasMany('summaryAttributes').value() === null) return changesets;

  SUMMARY_ATTRIBUTE_STATES.forEach((state) => {
    const attribute = summaryPage.summaryAttributes.find((summaryAttribute) => summaryAttribute.state === state);
    if (attribute) {
      changesets[state] = buildSummaryAttributeChangeset(attribute);
    }
  });

  return changesets;
}
